// src/pages/ThankYouPage.jsx
import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Paper, 
  useTheme,
  Avatar,
  Divider,
  Fade,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { styled } from '@mui/material/styles';

// Styled components con Material 3
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  textAlign: 'center',
  borderRadius: 24,
  maxWidth: 500,
  margin: '0 auto',
  overflow: 'hidden',
  boxShadow: theme.shadows[4],
  position: 'relative',
}));

const SuccessAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.success.main,
  width: 80,
  height: 80,
  margin: '0 auto',
  marginBottom: theme.spacing(3),
  boxShadow: theme.shadows[3],
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 28,
  padding: '12px 32px',
  fontWeight: 500,
  marginTop: theme.spacing(4),
  boxShadow: theme.shadows[2],
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const BrandBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(4),
}));

const BrandAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  marginRight: theme.spacing(1.5),
}));

const ThankYouPage = () => {
  const theme = useTheme();
  const [showCloseAlert, setShowCloseAlert] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(3);

  // Efecto para intentar cerrar la ventana automáticamente después de 30 segundos
  useEffect(() => {
    const timer = setTimeout(() => {
      handleCloseWindow();
    }, 30000);

    // Actualizar el contador cada segundo
    const countdownTimer = setInterval(() => {
      setCountdown(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => {
      clearTimeout(timer);
      clearInterval(countdownTimer);
    };
  }, []);

  // Efecto para la cuenta regresiva de redirección
  useEffect(() => {
    if (isRedirecting && redirectCountdown > 0) {
      const timer = setTimeout(() => {
        setRedirectCountdown(prev => prev - 1);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else if (isRedirecting && redirectCountdown === 0) {
      window.location.href = 'https://www.google.com';
    }
  }, [isRedirecting, redirectCountdown]);

  // Función para volver a la página principal
  const handleBackToHome = () => {
    window.location.href = '/';
  };

  // Función mejorada para cerrar la ventana
  const handleCloseWindow = () => {
    try {
      // Intentar cerrar la ventana
      window.close();
      
      // Verificar si la ventana se cerró (esto varía según el navegador)
      setTimeout(() => {
        // Si después de un breve tiempo seguimos aquí, la ventana no se cerró
        if (document.hasFocus()) {
          setShowCloseAlert(true);
          setIsRedirecting(true);
        }
      }, 300);
    } catch (e) {
      console.log("No se pudo cerrar la ventana automáticamente", e);
      setShowCloseAlert(true);
      setIsRedirecting(true);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: { xs: 4, sm: 8 }, mb: 4, px: 2 }}>
      <Fade in={true} timeout={800}>
        <StyledPaper elevation={3}>
          <BrandBox>
            <BrandAvatar>
              <MedicalServicesIcon />
            </BrandAvatar>
            <Typography variant="h6" component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>
              MedifyHC
            </Typography>
          </BrandBox>

          <SuccessAvatar>
            <CheckCircleIcon sx={{ fontSize: 48 }} />
          </SuccessAvatar>
          
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              fontWeight: 600,
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2
            }}
          >
            Consulta Finalizada
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mb: 3, color: 'text.secondary' }}>
            Gracias por utilizar nuestro servicio de telemedicina. Su consulta ha finalizado correctamente.
          </Typography>
          
          <Divider sx={{ my: 3, width: '60%', mx: 'auto' }} />
          
          <Box sx={{ bgcolor: 'background.subtle', py: 2, px: 3, borderRadius: 3, mb: 3 }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
              Puede cerrar esta ventana de forma segura o será cerrada automáticamente en {countdown} segundos.
            </Typography>
          </Box>
          
          {isRedirecting ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
              <CircularProgress size={24} sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Redirigiendo a Google en {redirectCountdown} segundos...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <ActionButton 
                variant="contained" 
                color="primary"
                startIcon={<CloseIcon />}
                onClick={handleCloseWindow}
              >
                Cerrar Ventana
              </ActionButton>
              
              <ActionButton 
                variant="outlined" 
                color="primary"
                onClick={handleBackToHome}
              >
                Volver al Inicio
              </ActionButton>
            </Box>
          )}
          
          <Typography 
            variant="caption" 
            display="block" 
            sx={{ mt: 4, color: 'text.secondary', fontSize: '0.75rem' }}
          >
            © {new Date().getFullYear()} MedifyHC - Todos los derechos reservados
          </Typography>
        </StyledPaper>
      </Fade>

      {/* Alerta cuando no se puede cerrar la ventana */}
      <Snackbar 
        open={showCloseAlert} 
        autoHideDuration={3000} 
        onClose={() => setShowCloseAlert(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowCloseAlert(false)} 
          severity="info" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          No se pudo cerrar la ventana. Redirigiendo a Google...
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ThankYouPage;
